import { toast } from "react-toastify";
import Api from "../core/data/redux/utils/apiCall";
import { BASE_URLS } from "../core/data/redux/apis/urls";

/**
 * Universal NOC Document Opener for Web.
 * Mirrors mobile app logic (handleViewDocument / handleViewDocumentWithBlobUtil)
 * with complete support for:
 * - Browser popup blocker bypass via pre-opened managed window
 * - Direct URLs (S3, HTTP, HTTPS, blob:)
 * - Embedded Base64 Content (PDF, PNG, JPEG, WEBP, etc.)
 * - Prioritized exact S3 keys (serverKey, key, awsKey)
 * - Complete candidate prefix paths for all NOC modules
 * - Fallbacks to SAP DMS docId endpoints (noc-outjob-doc-by-docid, noc-document-by-id)
 * - Chat attachments retrieval fallback
 */
export const handleDocumentClick = async (
  docParam: any,
  nocType?: string,
  onShowToast?: (
    title: string,
    message: string,
    type: "success" | "danger" | "warning" | "primary"
  ) => void
) => {
  if (!docParam) {
    if (onShowToast) {
      onShowToast("Warning", "Document file information is missing", "warning");
    } else {
      toast.warning("Document file information is missing");
    }
    return;
  }

  // 1. Synchronously open a new blank tab to bypass modern browser popup blockers
  const popup = window.open("", "_blank");
  if (popup) {
    popup.document.title = "Loading document...";
    popup.document.body.innerHTML = `
      <div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#334155;background:#f8fafc;margin:0;">
        <div style="text-align:center;padding:32px;background:#ffffff;border-radius:16px;box-shadow:0 10px 25px -5px rgba(0,0,0,0.1);max-width:440px;width:90%;">
          <div style="width:40px;height:40px;border:3px solid #e2e8f0;border-top-color:#dc3545;border-radius:50%;margin:0 auto 16px;animation:spin 0.8s linear infinite;"></div>
          <div style="font-size:16px;font-weight:600;margin-bottom:6px;color:#0f172a;">Loading document...</div>
          <div style="font-size:13px;color:#64748b;line-height:1.5;">Please wait while your document is being prepared.</div>
        </div>
      </div>
      <style>@keyframes spin{to{transform:rotate(360deg)}}</style>
    `;
  }

  const notifyError = (msg: string) => {
    if (popup && !popup.closed) {
      popup.close();
    }
    if (onShowToast) {
      onShowToast("Error", msg, "danger");
    } else {
      toast.error(msg);
    }
  };

  const getMimeType = (filename: string, fileExtension?: string): string => {
    const ext = (fileExtension || filename.split(".").pop() || "pdf").toLowerCase().replace(/^\./, "");
    if (ext === "pdf" || ext.includes("pdf")) return "application/pdf";
    if (["jpg", "jpeg"].includes(ext) || ext.includes("jp")) return "image/jpeg";
    if (ext === "png" || ext.includes("png")) return "image/png";
    if (ext === "webp") return "image/webp";
    if (ext === "gif") return "image/gif";
    if (ext.includes("/")) return ext;
    return `application/${ext}`;
  };

  try {
    // 2. Direct File or Blob object check
    if (typeof window !== "undefined" && (docParam instanceof File || docParam instanceof Blob)) {
      const blobUrl = URL.createObjectURL(docParam);
      if (popup && !popup.closed) {
        popup.location.href = blobUrl;
      } else {
        window.open(blobUrl, "_blank");
      }
      return;
    }

    // 2b. Direct HTTP/HTTPS/blob URL check
    const directUrl =
      typeof docParam === "string" &&
      (docParam.startsWith("http://") || docParam.startsWith("https://") || docParam.startsWith("blob:"))
        ? docParam
        : typeof docParam === "object"
          ? docParam.attachmentUrl ||
            docParam.url ||
            docParam.serverURL ||
            docParam.serverUrl ||
            docParam.fileUrl ||
            docParam.s3Url ||
            docParam.downloadUrl ||
            docParam.signedUrl
          : null;

    if (
      directUrl &&
      typeof directUrl === "string" &&
      (directUrl.startsWith("http://") || directUrl.startsWith("https://") || directUrl.startsWith("blob:"))
    ) {
      if (popup && !popup.closed) {
        popup.location.href = directUrl;
      } else {
        window.open(directUrl, "_blank");
      }
      return;
    }

    // 3. Embedded base64 Content check (SAP DMS / DB streams)
    const content =
      typeof docParam === "object"
        ? docParam.Content || docParam.content || docParam.FileContent || docParam.fileContent
        : null;

    if (content && typeof content === "string" && content.trim() !== "") {
      try {
        const rawName =
          typeof docParam === "object"
            ? docParam.fileName || docParam.FileName || docParam.name || "document.pdf"
            : "document.pdf";
        const docExt =
          typeof docParam === "object"
            ? docParam.fileExtension || docParam.FileExtension || docParam.mimeType
            : undefined;
        const mimeType = getMimeType(rawName, docExt);

        const cleanBase64 = content.trim().replace(/^data:.*?;base64,/, "").replace(/^data:.*?,/, "");
        const byteCharacters = atob(cleanBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });
        const blobUrl = URL.createObjectURL(blob);
        if (popup && !popup.closed) {
          popup.location.href = blobUrl;
        } else {
          window.open(blobUrl, "_blank");
        }
        return;
      } catch (base64Err) {
        console.warn("Error decoding embedded base64 content:", base64Err);
      }
    }

    // 4. Extract serverKey and fileName
    const serverKey =
      typeof docParam === "object"
        ? docParam.serverKey || docParam.key || docParam.awsKey || ""
        : typeof docParam === "string" && docParam.includes("/") && !docParam.startsWith("http")
          ? docParam
          : "";

    const rawFileName =
      typeof docParam === "string"
        ? docParam
        : docParam?.fileName ||
          docParam?.FileName ||
          docParam?.name ||
          serverKey ||
          docParam?.UUID ||
          docParam?.docID ||
          "";

    const fileName = String(rawFileName).trim();

    // If fileName or serverKey is actually a direct URL
    if (fileName.startsWith("http://") || fileName.startsWith("https://")) {
      if (popup && !popup.closed) {
        popup.location.href = fileName;
      } else {
        window.open(fileName, "_blank");
      }
      return;
    }
    if (serverKey.startsWith("http://") || serverKey.startsWith("https://")) {
      if (popup && !popup.closed) {
        popup.location.href = serverKey;
      } else {
        window.open(serverKey, "_blank");
      }
      return;
    }

    const fileBasename = (fileName || serverKey).split("/").pop() || fileName || serverKey;

    // 4b. If docID is present (standard for submitted SAP DMS documents on approver & details pages),
    // try the authoritative SAP DMS endpoints FIRST (mirrors mobile handleViewDocument logic)
    const docID =
      typeof docParam === "object"
        ? docParam.docID || docParam.docId || (typeof docParam.id === "string" && docParam.id.length > 10 ? docParam.id : null)
        : null;

    const typeStr = String(nocType || "").toUpperCase().replace(/[\s_]+/g, "-");
    const isReimb = typeStr.includes("REIMB");
    const isHigherEdu =
      typeStr.includes("STUD") || typeStr.includes("EDU") || typeStr.includes("HIGHER");
    const isExIndia =
      typeStr.includes("EXINDIA") ||
      typeStr.includes("EX-INDIA") ||
      typeStr.includes("FOREIGN") ||
      typeStr.includes("VISIT");
    const isPassport = typeStr.includes("PASSPORT") || typeStr.includes("VISA");
    const isOnlineCourse = !isReimb && (typeStr.includes("ONLINE") || typeStr.includes("COURSE"));
    const isOutjob = typeStr.includes("OUTJOB") || typeStr.includes("OUTSIDE");

    if (docID) {
      const endpoints = isOutjob
        ? ["noc/noc-outjob-doc-by-docid", "noc/noc-document-by-id"]
        : ["noc/noc-document-by-id", "noc/noc-outjob-doc-by-docid"];

      for (const ep of endpoints) {
        try {
          const docRes: any = await Api.post(`${BASE_URLS.BASE_URL}${ep}`, { docId: docID });
          const docData =
            docRes?.data?.d?.results?.[0] ||
            docRes?.data?.data?.d?.results?.[0] ||
            docRes?.data?.data?.[0] ||
            docRes?.data?.data ||
            docRes?.data?.results?.[0] ||
            docRes?.data;
          const base64Content = docData?.Content || docData?.FileContent || docData?.content || docData?.fileContent;
          if (base64Content && typeof base64Content === "string" && base64Content.trim() !== "") {
            const rawName = docData.FileName || docData.fileName || fileBasename || "document.pdf";
            const mimeType = getMimeType(rawName, docData.FileExtension);
            const cleanBase64 = String(base64Content).trim().replace(/^data:.*?;base64,/, "").replace(/^data:.*?,/, "");
            const byteCharacters = atob(cleanBase64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mimeType });
            const blobUrl = URL.createObjectURL(blob);
            if (popup && !popup.closed) {
              popup.location.href = blobUrl;
            } else {
              window.open(blobUrl, "_blank");
            }
            return;
          }
        } catch (apiErr) {
          console.warn(`DocID fetch error on ${ep}:`, apiErr);
        }
      }
    }

    if (!fileBasename && !serverKey) {
      notifyError("Document identifier is missing");
      return;
    }

    // 5. Build candidate S3 keys based on NOC module type
    const candidateKeys: string[] = [];

    // Prioritize exact serverKey if known
    if (serverKey) {
      candidateKeys.push(serverKey);
    }

    // Prioritize fileName if it contains a folder path
    if (fileName && fileName.includes("/") && fileName !== serverKey) {
      candidateKeys.push(fileName);
    }

    // Module-specific candidate prefixes
    if (fileBasename) {
      // In web app, files uploaded via uploadFileToAWS are placed directly at root (fileBasename).
      // Prioritize raw fileBasename before guessed folder prefixes!
      candidateKeys.push(fileBasename);

      if (isOutjob) {
        candidateKeys.push(`NOC_OUTSIDE_JOB/${fileBasename}`);
        candidateKeys.push(`NOC_OUTJOB/${fileBasename}`);
        candidateKeys.push(`outside_job_doc/${fileBasename}`);
        candidateKeys.push(`noc_outjob_doc/${fileBasename}`);
      } else if (isReimb) {
        candidateKeys.push(`reimbursement_doc/${fileBasename}`);
        candidateKeys.push(`NOC_REIMBURSEMENT/${fileBasename}`);
        candidateKeys.push(`noc-reimbursement/${fileBasename}`);
        candidateKeys.push(`online_course_reimb_doc/${fileBasename}`);
        candidateKeys.push(`NOC_ONLINECOURSE/${fileBasename}`);
      } else if (isHigherEdu) {
        candidateKeys.push(`NOC_HIGHER_EDU/${fileBasename}`);
        candidateKeys.push(`higher_edu_doc/${fileBasename}`);
        candidateKeys.push(`noc_higher_edu_doc/${fileBasename}`);
        candidateKeys.push(`HIGHER-STUDIES/${fileBasename}`);
      } else if (isExIndia) {
        candidateKeys.push(`NOC_EXINDIA/${fileBasename}`);
        candidateKeys.push(`ex_india_doc/${fileBasename}`);
        candidateKeys.push(`noc_exindia_doc/${fileBasename}`);
      } else if (isPassport) {
        candidateKeys.push(`NOC_PASSPORT/${fileBasename}`);
        candidateKeys.push(`passport_doc/${fileBasename}`);
        candidateKeys.push(`visa_passport_doc/${fileBasename}`);
        candidateKeys.push(`VISA-PASSPORT/${fileBasename}`);
      } else if (isOnlineCourse) {
        candidateKeys.push(`NOC_ONLINECOURSE/${fileBasename}`);
        candidateKeys.push(`online_course_doc/${fileBasename}`);
      }

      // Universal fallbacks
      candidateKeys.push(`noc_doc/${fileBasename}`);
      candidateKeys.push(`documents/${fileBasename}`);
      candidateKeys.push(`chat/${fileBasename}`);
    }

    // Deduplicate
    const uniqueCandidates = Array.from(new Set(candidateKeys)).filter(Boolean);

    // 6. Query S3 Presigned URL endpoint with object existence verification
    let firstPresignedUrl = "";
    for (const key of uniqueCandidates) {
      try {
        const endPoint = `${BASE_URLS.BASE_URL}aws-bucket/get-presigned-url`;
        const res: any = await Api.post(endPoint, { key });
        const resData = res?.data;

        let pUrl = "";
        if (typeof resData === "string" && (resData.startsWith("http://") || resData.startsWith("https://"))) {
          pUrl = resData;
        } else if (
          typeof resData?.data === "string" &&
          (resData.data.startsWith("http://") || resData.data.startsWith("https://"))
        ) {
          pUrl = resData.data;
        } else if (typeof resData?.data?.url === "string") {
          pUrl = resData.data.url;
        } else if (typeof resData?.url === "string") {
          pUrl = resData.url;
        } else if (typeof resData?.presignedUrl === "string") {
          pUrl = resData.presignedUrl;
        }

        if (pUrl && (pUrl.startsWith("http://") || pUrl.startsWith("https://"))) {
          // If we have multiple candidates, verify if this S3 key exists before navigating
          if (uniqueCandidates.length > 1) {
            try {
              const headRes = await fetch(pUrl, { method: "HEAD" });
              if (headRes.ok) {
                // Object exists in S3!
                if (popup && !popup.closed) {
                  popup.location.href = pUrl;
                } else {
                  window.open(pUrl, "_blank");
                }
                return;
              }
              // If status is 404 or 403 (NoSuchKey), this candidate key doesn't exist in S3.
              // Continue to next candidate!
              continue;
            } catch (headErr) {
              // In case fetch is blocked by browser CORS on error, record first presigned URL as fallback
              if (!firstPresignedUrl) {
                firstPresignedUrl = pUrl;
              }
            }
          } else {
            // Single candidate: open directly
            if (popup && !popup.closed) {
              popup.location.href = pUrl;
            } else {
              window.open(pUrl, "_blank");
            }
            return;
          }
        }
      } catch (keyErr) {
        // Continue to next candidate key
      }
    }

    // If none of the candidates was verified 200 via HEAD, but we got a presigned URL, open the fallback
    if (firstPresignedUrl) {
      if (popup && !popup.closed) {
        popup.location.href = firstPresignedUrl;
      } else {
        window.open(firstPresignedUrl, "_blank");
      }
      return;
    }

    notifyError("Unable to open file. Document link is unavailable or expired.");
  } catch (error) {
    console.error("Error opening document:", error);
    notifyError("Unable to open file. Please try again.");
  }
};
